import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight, Check, Shield, ClipboardCheck, Users, MapPin } from 'lucide-react';

const services = [
  {
    id: 'landscape',
    title: 'Landscape Maintenance',
    path: '/landscape-maintenance',
    description: 'Structured programs designed to protect long-term asset value through systematic grounds management.',
    image: 'https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/r7pg4pr4_iStock-2155879454.jpg',
    features: [
      'Scheduled turf care and mowing programs',
      'Tree and shrub management cycles',
      'Seasonal planting and enhancement',
      'Weed control and fertilization protocols',
      'Debris management and site cleanliness'
    ]
  },
  {
    id: 'irrigation',
    title: 'Irrigation Management',
    path: '/irrigation-management',
    description: 'Systematic monitoring and maintenance to optimize water efficiency and protect plant investments.',
    image: 'https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/y7iby1hw_iStock-1428027852.jpg',
    features: [
      'System inspections and performance audits',
      'Controller programming and seasonal adjustment',
      'Head and line repairs',
      'Water efficiency monitoring',
      'Backflow testing coordination'
    ]
  },
  {
    id: 'pressure-washing',
    title: 'Pressure Washing',
    path: '/pressure-washing',
    description: 'Cyclical surface cleaning programs to maintain property standards and extend infrastructure lifespan.',
    image: 'https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/mrf246m2_iStock-2155879493.jpg',
    features: [
      'Walkway and sidewalk cleaning cycles',
      'Parking structure maintenance',
      'Building exterior cleaning',
      'Stain and contaminant removal',
      'Scheduled cleaning programs'
    ]
  },
  {
    id: 'exterior',
    title: 'Exterior Asset Care',
    path: '/exterior-asset-care',
    description: 'Scheduled cleaning and aesthetic care of exterior assets including gates, signage, lighting fixtures, and other property elements.',
    image: 'https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/e0gkd1b5_iStock-2175972769.jpg',
    features: [
      'Gate and fence cleaning',
      'Garage door cleaning',
      'Signage cleaning and polishing',
      'Pool cover and tile trim cleaning',
      'Lighting fixture cleaning'
    ]
  }
];

// Hero Section
const ServicesHero = () => (
  <section 
    className="relative min-h-[500px] flex items-center"
    data-testid="services-hero"
  >
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/2fol7ywi_iStock-2175972856.jpg')`
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-[#1a2e44]/90 via-[#1a2e44]/75 to-[#1a2e44]/50" />
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="max-w-2xl">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight uppercase tracking-tight mb-6">
          Our Services
        </h1>
        <p className="text-xl text-slate-200 mb-8">
          Structured exterior property management programs designed to protect asset value and maintain property standards.
        </p>
        <Link
          to="/contact?intent=assessment"
          className="inline-block bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-bold py-4 px-8 uppercase tracking-wide text-sm transition-all hover:scale-[1.02]"
          data-testid="hero-cta"
        >
          Get a Free Site Assessment
        </Link>
      </div>
    </div>
  </section>
);

// Service Detail Section
const ServiceDetail = ({ service, index }) => {
  const isEven = index % 2 === 0;
  
  return (
    <section 
      id={service.id}
      className={`py-20 ${isEven ? 'bg-white' : 'bg-slate-50'}`}
      data-testid={`service-${service.id}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
          {/* Image */}
          <div className={!isEven ? 'lg:order-2' : ''}>
            <div className="relative overflow-hidden">
              <img 
                src={service.image}
                alt={service.title}
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute bottom-0 left-0 w-full h-2 bg-[#1a2e44]" />
            </div>
          </div>

          {/* Content */}
          <div className={!isEven ? 'lg:order-1' : ''}>
            <Link 
              to={service.path}
              className="inline-block group"
              data-testid={`service-title-link-${service.id}`}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e44] mb-4 group-hover:text-[#1a2e44]/70 transition-colors">
                {service.title}
                <ArrowRight className="inline-block w-6 h-6 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
              </h2>
            </Link>
            <p className="text-lg text-slate-600 mb-8">
              {service.description}
            </p>

            <ul className="space-y-3 mb-8">
              {service.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-slate-700">{feature}</span>
                </li>
              ))}
            </ul>

            <Link
              to={`/contact?intent=assessment&service=${service.id}`}
              className="inline-flex items-center gap-2 bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-bold py-3 px-6 uppercase tracking-wide text-sm transition-all"
              data-testid={`cta-${service.id}`}
            >
              Request a Site Assessment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Trust Section
const TrustSection = () => {
  const trustItems = [
    { icon: Shield, title: 'Licensed & Insured', description: 'Full commercial liability coverage.' },
    { icon: ClipboardCheck, title: 'Structured Protocols', description: 'Consistent, reliable service delivery.' },
    { icon: Users, title: 'Dedicated Oversight', description: 'Responsive account management.' },
    { icon: MapPin, title: 'Local Expertise', description: 'Serving Pasadena & LA County.' }
  ];

  return (
    <section className="bg-[#1a2e44] py-16" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {trustItems.map((item, index) => (
            <div key={index} className="text-center">
              <item.icon className="w-10 h-10 text-white mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="text-base font-bold text-white mb-1">{item.title}</h3>
              <p className="text-slate-400 text-sm">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA
const ServicesCTA = () => (
  <section className="py-20 bg-slate-50" data-testid="services-cta">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e44] mb-4">
        Ready to Protect Your Property Investment?
      </h2>
      <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
        Schedule a site assessment to discuss your property's requirements.
      </p>
      <Link
        to="/contact?intent=assessment"
        className="inline-flex items-center gap-2 bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-bold py-4 px-8 uppercase tracking-wide text-sm transition-all"
        data-testid="services-cta-btn"
      >
        Request a Site Assessment <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

export default function ServicesPage() {
  useEffect(() => {
    document.title = 'Our Services | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="services-page">
      <ServicesHero />
      {services.map((service, index) => (
        <ServiceDetail key={service.id} service={service} index={index} />
      ))}
      <TrustSection />
      <ServicesCTA />
    </div>
  );
}
